import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import React from 'react'
import { UtilityToolbarDropdownChildProps } from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import { useTranslate } from 'src/localization/Trns'

import style from './SavedViewsDropdown.style'

// The dropdown only renders each view's id and display name, so it accepts any
// saved-view-like shape. This keeps it reusable across view types (transactions,
// card feed, …) without unsafe casts at the call site.
export interface SavedViewOption {
  id: string
  displayName: string
}

interface SavedViewsDropdownProps extends UtilityToolbarDropdownChildProps {
  handleSetView: (viewId: string) => void
  handleResetToDefault: () => void
  handleSaveNewView: () => void
  handleDeleteView: (viewId: string) => void
  views: SavedViewOption[]
  selectedViewId?: string
  hasViewChange: boolean
}

const SavedViewsDropdown: React.FC<SavedViewsDropdownProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const handleViewClick = (e: React.SyntheticEvent, id: string): void => {
    e.stopPropagation()
    props.handleSetView(id)
    props.close?.()
  }

  const handleDefaultViewClick = (e: React.SyntheticEvent): void => {
    e.stopPropagation()
    props.handleResetToDefault()
    props.close?.()
  }

  const systemDefaultViewIsSelected =
    props.selectedViewId === undefined && !props.hasViewChange

  const options = props.views

  return (
    <div className="SavedViewsDropdown" css={style}>
      <div className={cn('SavedViewsDropdown', 'container')}>
        <div className={cn('SavedViewsDropdown', 'options')}>
          <div className={cn('option-title')}>
            <span className={'text-secondary font-md'}>
              {translate('Views')}
            </span>
          </div>
          <div
            key={'system-default-option'}
            className={cn('option', {
              active: systemDefaultViewIsSelected,
            })}
            role="button"
            tabIndex={0}
            onClick={(e): void => handleDefaultViewClick(e)}
            onKeyDown={(e): void => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleDefaultViewClick(e)
              }
            }}
          >
            <span className={'text-primary font-md'}>
              {translate('Default View')}
            </span>
          </div>
          {options.map((option) => (
            <div
              className={cn('option', {
                // Only highlight the applied view while it is clean. Once the
                // user edits a filter/sort it becomes an unsaved draft, so drop
                // the active state — consistent with the "Unsaved View" label.
                active:
                  props.selectedViewId === option.id && !props.hasViewChange,
              })}
              key={option.id}
              onClick={(e): void => handleViewClick(e, option.id)}
              onKeyDown={(e): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleViewClick(e, option.id)
                }
              }}
              role="button"
              tabIndex={0}
            >
              <span className={'text-primary font-md'}>
                {option.displayName}
              </span>
              <Button
                variant={'no-style'}
                className={'delete-button'}
                data-testId={`deleteViewBtn-${option.id}`}
                aria-label={translate('Delete View')}
                onClick={(e) => {
                  e.stopPropagation()
                  props.handleDeleteView(option.id)
                  props.close?.()
                }}
              >
                <SvgTrash />
              </Button>
            </div>
          ))}
        </div>
        <div className={'save-container'}>
          <Button
            onClick={(): void => {
              props.handleSaveNewView()
              props.close?.()
            }}
            className={'save-button'}
            variant={'no-border'}
          >
            {translate('+ Save View')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SavedViewsDropdown
